import type { LeagueCreationType } from "../common/types.ts";

const leagueCreationTypes = ["random", "real"] as const satisfies LeagueCreationType[];

const isLeagueCreationType = (value: unknown): value is LeagueCreationType =>
	value === "random" || value === "real";

export class DuplicateLeagueCreationTypeError extends Error {
	readonly creationType: LeagueCreationType;

	constructor(creationType: LeagueCreationType) {
		super(
			`You already have a ${creationType} league. Delete it before creating another one.`,
		);
		this.name = "DuplicateLeagueCreationTypeError";
		this.creationType = creationType;
	}
}

export const getLeagueCreationTypeFromCreateParam = (
	param: unknown,
): LeagueCreationType => {
	if (typeof param !== "object" || param === null) {
		return "random";
	}

	const getLeagueOptions = (param as { getLeagueOptions?: unknown })
		.getLeagueOptions;
	if (typeof getLeagueOptions !== "object" || getLeagueOptions === null) {
		return "random";
	}

	return (getLeagueOptions as { type?: unknown }).type === "real"
		? "real"
		: "random";
};

export const getExistingLeagueCreationTypes = (
	leagues: { creationType?: unknown }[],
): LeagueCreationType[] => {
	const types = new Set<LeagueCreationType>();

	for (const league of leagues) {
		if (isLeagueCreationType(league.creationType)) {
			types.add(league.creationType);
		} else {
			for (const type of leagueCreationTypes) {
				types.add(type);
			}
		}
	}

	return leagueCreationTypes.filter((type) => types.has(type));
};

export const assertCanCreateLeagueType = (
	leagues: { creationType?: unknown }[],
	creationType: LeagueCreationType,
) => {
	if (getExistingLeagueCreationTypes(leagues).includes(creationType)) {
		throw new DuplicateLeagueCreationTypeError(creationType);
	}
};
